/**
 * Marketer View — Layer 2 subscription gating (SYR-68).
 *
 * Two-layer gating model:
 *  - Layer 1 (element locking) is API-enforced: `/brand-equity/calculate`
 *    returns `locked: boolean` per element; the UI hides the score behind a
 *    lock badge. That is always on and handled at render time, not here.
 *  - Layer 2 (this file) gates marketer-view-SPECIFIC features (AI narrative,
 *    recommendations, action plan, benchmarks, expert, …) against the
 *    subscription tier. It is defined now but DISABLED for review: while
 *    `MARKETER_GATING_REVIEW_MODE` is true the full experience is visible so
 *    Adir can review every feature. Flip the flag to enforce the matrix once
 *    the tier mapping is signed off.
 *
 * The matrix below is the single source of truth — adjust the required
 * `SubscriptionFeature` per marketer feature here and nowhere else.
 */

import { SubscriptionFeature } from '../../../hooks/useSubscriptionFeatures';

export type MarketerFeature =
  | 'briefing.oneThing' // AI "The one thing to know" hero
  | 'briefing.theFix' // AI "fix first" narrative
  | 'briefing.recommendations' // AI leverage-ranked recommendations
  | 'briefing.commentary' // per-booster / per-signal AI commentary
  | 'actionPlan' // committed plays + task tracking
  | 'benchmarks' // The Benchmarks → Benchmarks
  | 'benchmarks.segmentComparison' // The Benchmarks → Trends → Segment Comparison
  | 'expert' // The Expert (AI chat)
  | 'detail' // The Detail (BE flow graph)
  | 'responses'; // The Responses (statement sentiment)

/**
 * Required subscription feature per marketer feature. This is the mapping Adir
 * will tune before enforcement; keep it conservative and easy to read.
 */
export const MARKETER_FEATURE_MATRIX: Record<
  MarketerFeature,
  SubscriptionFeature
> = {
  'briefing.oneThing': SubscriptionFeature.BasicAI,
  'briefing.theFix': SubscriptionFeature.BasicAI,
  'briefing.recommendations': SubscriptionFeature.AdvancedAI,
  'briefing.commentary': SubscriptionFeature.BasicAI,
  actionPlan: SubscriptionFeature.AdvancedAI,
  benchmarks: SubscriptionFeature.Benchmarking,
  'benchmarks.segmentComparison': SubscriptionFeature.Benchmarking,
  expert: SubscriptionFeature.BasicAI,
  detail: SubscriptionFeature.BrandEquity,
  responses: SubscriptionFeature.BrandEquity,
};

/**
 * Visible-for-review flag. While `true`, Layer 2 gating is NOT enforced — every
 * marketer feature is visible regardless of tier. Set to `false` to enforce
 * `MARKETER_FEATURE_MATRIX`. (Layer 1 element locking is unaffected either way.)
 */
export const MARKETER_GATING_REVIEW_MODE = true;

/** Where the "Upgrade to unlock" CTA routes. */
export const MARKETER_UPGRADE_ROUTE =
  '/dashboard/organisation?tab=billing#upgrade';
